import { getCache, storeInCache } from 'src/hooks/useStoreCache';
import { useApiWrapper } from './useApiWrapper';
import {
  ApiResponse,
  ApplicationStatusInfo,
  PageUsageInfo,
  AuthSessionsInfo,
  TransactionTypes,
  WebOutageInfo,
  ApplicationInfo,
  PaymentInfo,
  ssmInfo,
  ApiError,
  AlertInfo,
  AlertBody,
  ReportHtmlInfo,
  ReportFilters,
  NonProdSecurityInfo,
  DeviceHealthInfo,
  ErrorBreakdownInfo,
  EventDiagnosticsInfo,
  PerformanceTrendInfo,
  OutageScreenshotSummary,
  OutageScreenshotDetail,
  TemporalInfo,
  SyntheticCanariesInfo,
  AppTogglesInfo,
  AppToggleChange,
  AppToggleWriteFailure
} from './types/types';
import { getDefaultTimeRangeValue } from 'src/utils/helpers';

/**
 * Amplify's API.post rejects on any non-2xx, surfacing the response axios-style.
 * The backend returns 403 for an authorization denial and 409 with a `reason` for the
 * two recoverable write collisions.
 */
const errorResponse = (err: unknown) =>
  (err as { response?: { status?: number; data?: { reason?: string } } })?.response;

const isNotAuthorized = (err: unknown) => errorResponse(err)?.status === 403;

/**
 * Maps a failed toggle write to the reason, so the page can tell the operator what to do
 * about it. The backend is the source of truth for `reason`; anything unrecognized is
 * reported as unknown rather than guessed at.
 */
export const classifyToggleWriteError = (err: unknown): AppToggleWriteFailure => {
  const response = errorResponse(err);
  if (response?.status === 403) return 'notAuthorized';
  if (response?.status === 409) {
    const reason = response?.data?.reason;
    if (reason === 'versionConflict') return 'versionConflict';
    if (reason === 'deploymentInProgress') return 'deploymentInProgress';
  }
  return 'unknown';
};

export const usePgeApi = () => {
  const { fetchPostApi } = useApiWrapper();
  const timeRangeValue = getDefaultTimeRangeValue();

  const fetchApplicationStatus = async (): Promise<
    ApiResponse<ApplicationStatusInfo[]>
  > => {
    try {
      const cacheKey = `ApplicationStatus-${timeRangeValue}`;
      const webOutageCacheValue = getCache(cacheKey);
      if (webOutageCacheValue) {
        return webOutageCacheValue;
      }

      const apiResponse = await fetchPostApi<
        ApiResponse<ApplicationStatusInfo[]>
      >({
        elasticMetricType: 'applications_status'
      });

      storeInCache(cacheKey, apiResponse);
      return apiResponse;
    } catch (err) {
      return {
        result: {
          errorMessage: 'Api Response error'
        } as ApiError,
        lastUpdated: ''
      };
    }
  };
  const fetchPageUsage = async (): Promise<ApiResponse<PageUsageInfo[]>> => {
    try {
      const cacheKey = `PageUsage-${timeRangeValue} `;
      const webOutageCacheValue = getCache(cacheKey);
      if (webOutageCacheValue) {
        return webOutageCacheValue;
      }

      const apiResponse = await fetchPostApi<ApiResponse<PageUsageInfo[]>>({
        elasticMetricType: 'page_usage'
      });

      storeInCache(cacheKey, apiResponse);
      return apiResponse;
    } catch (err) {
      return {
        result: {
          errorMessage: 'Api Response error'
        } as ApiError,
        lastUpdated: ''
      };
    }
  };

  const fetchAuthSessions = async (): Promise<
    ApiResponse<AuthSessionsInfo[]>
  > => {
    try {
      const cacheKey = `AuthSessions-${timeRangeValue}`;
      const cached = getCache(cacheKey);
      if (cached) return cached;

      const apiResponse = await fetchPostApi<ApiResponse<AuthSessionsInfo[]>>({
        elasticMetricType: 'page_usage',
        elasticMetricSubType: 'login-success-failure'
      });

      storeInCache(cacheKey, apiResponse);
      return apiResponse;
    } catch (err) {
      return {
        result: { errorMessage: 'Api Response error' } as ApiError,
        lastUpdated: ''
      };
    }
  };

  const fetchTransactionInfo = async <T>(
    isRequiredAllDetails: boolean = false,
    type: TransactionTypes = TransactionTypes.VOLUME
  ): Promise<ApiResponse<T>> => {
    try {
      const cacheKey = `transactionInfo-${timeRangeValue} ${type} ${
        isRequiredAllDetails ? ':details' : ''
      }`;
      const transactionCacheValue = getCache(cacheKey);
      if (transactionCacheValue) {
        return transactionCacheValue;
      }

      const apiResponse = await fetchPostApi<ApiResponse<T>>({
        elasticMetricType: 'transactions',
        isRequiredAllDetails,
        type
      });

      storeInCache(cacheKey, apiResponse);
      return apiResponse;
    } catch (err) {
      return {
        result: {
          errorMessage: 'Api Response error'
        } as ApiError,
        lastUpdated: ''
      };
    }
  };
  const fetchWebOutageInfo = async (
    isRequiredAllDetails: boolean = false
  ): Promise<ApiResponse<WebOutageInfo>> => {
    try {
      const cacheKey = `webOutageInfo-${timeRangeValue} ${
        isRequiredAllDetails ? ':details' : ''
      }`;
      const webOutageCacheValue = getCache(cacheKey);
      if (webOutageCacheValue) {
        return webOutageCacheValue;
      }

      const apiResponse = await fetchPostApi<ApiResponse<WebOutageInfo>>({
        elasticMetricType: 'webOutage',
        isRequiredAllDetails
      });

      storeInCache(cacheKey, apiResponse);
      return apiResponse;
    } catch (err) {
      return {
        result: {
          errorMessage: 'Api Response error'
        } as ApiError,
        lastUpdated: ''
      };
    }
  };

  const fetchOutageScreenshots = async (): Promise<
    ApiResponse<OutageScreenshotSummary[]>
  > => {
    try {
      const apiResponse = await fetchPostApi<
        ApiResponse<OutageScreenshotSummary[]>
      >({
        elasticMetricType: 'outage_screenshot'
      });
      return apiResponse;
    } catch (err) {
      return {
        result: { errorMessage: 'Failed to load outage screenshots' } as ApiError,
        lastUpdated: ''
      };
    }
  };

  const fetchOutageScreenshotDetail = async (
    checkGroup: string
  ): Promise<ApiResponse<OutageScreenshotDetail>> => {
    try {
      const apiResponse = await fetchPostApi<ApiResponse<OutageScreenshotDetail>>({
        elasticMetricType: 'outage_screenshot',
        body: { checkGroup }
      });
      return apiResponse;
    } catch (err) {
      return {
        result: { errorMessage: 'Failed to load screenshot' } as ApiError,
        lastUpdated: ''
      };
    }
  };

  const fetchApplications = async (
    isRequiredAllDetails: boolean = false
  ): Promise<ApiResponse<ApplicationInfo[]>> => {
    try {
      const cacheKey = `getApplicationInfo-${timeRangeValue} ${
        isRequiredAllDetails ? ':details' : ''
      }`;
      const getApplicationInfoCacheValue = getCache(cacheKey);
      if (getApplicationInfoCacheValue) {
        return getApplicationInfoCacheValue;
      }
      const apiResponse = await fetchPostApi<ApiResponse<ApplicationInfo[]>>({
        elasticMetricType: 'apm-service',
        isRequiredAllDetails
      });

      storeInCache(cacheKey, apiResponse);
      return apiResponse;
    } catch (err) {
      return {
        result: {
          errorMessage: 'Api Response error'
        } as ApiError,
        lastUpdated: ''
      };
    }
  };

  const fetchPayment = async (
    isRequiredAllDetails: boolean = false
  ): Promise<ApiResponse<PaymentInfo>> => {
    try {
      const cacheKey = `getPayments-${timeRangeValue} ${
        isRequiredAllDetails ? ':details' : ''
      }`;
      const getPaymentCacheValue = getCache(cacheKey);
      if (getPaymentCacheValue) {
        return getPaymentCacheValue;
      }
      const apiResponse = await fetchPostApi<ApiResponse<PaymentInfo>>({
        elasticMetricType: 'payment',
        isRequiredAllDetails
      });

      storeInCache(cacheKey, apiResponse);
      return apiResponse;
    } catch (err) {
      return {
        result: {
          errorMessage: 'Api Response error'
        } as ApiError,
        lastUpdated: ''
      };
    }
  };

  const fetchTransactions = async (
    isRequiredAllDetails: boolean = false
  ): Promise<ApiResponse<ssmInfo[]>> => {
    try {
      const cacheKey = `getSSMInfo-${timeRangeValue} ${
        isRequiredAllDetails ? ':details' : ''
      }`;
      const getSSMInfoCacheValue = getCache(cacheKey);
      if (getSSMInfoCacheValue) {
        return getSSMInfoCacheValue;
      }

      const apiResponse = await fetchPostApi<ApiResponse<ssmInfo[]>>({
        elasticMetricType: 'ssm',
        isRequiredAllDetails
      });

      storeInCache(cacheKey, apiResponse);
      return apiResponse;
    } catch (err) {
      return {
        result: {
          errorMessage: 'Api Response error'
        } as ApiError,
        lastUpdated: ''
      };
    }
  };

  const fetchAlertsInfo = async (
    type = 'getAlerts'
  ): Promise<ApiResponse<AlertInfo[]>> => {
    const apiResponse = await fetchPostApi<ApiResponse<AlertInfo[]>>({
      elasticMetricType: 'Alerts',
      isRequiredAllDetails: false,
      type
    });
    return apiResponse;
  };

  const fetchReport = async (
    filters: ReportFilters = {},
    skipCache = false
  ): Promise<ApiResponse<ReportHtmlInfo>> => {
    try {
      const filterKey = Object.entries(filters)
        .filter(([, v]) => v)
        .map(([k, v]) => `${k}:${v}`)
        .join('|');
      const cacheKey = `report-${filterKey || 'default'}`;

      if (!skipCache) {
        const cached = getCache(cacheKey);
        if (cached) return cached;
      }

      const filterBody: Record<string, unknown> = {};
      if (filters.env) filterBody.reportEnv = filters.env;
      if (filters.delivery_type)
        filterBody.reportDeliveryType = filters.delivery_type;
      if (filters.notification_type)
        filterBody.reportNotificationType = filters.notification_type;
      if (filters.mode) filterBody.reportMode = filters.mode;
      if (filters.start_date) filterBody.reportStartDate = filters.start_date;
      if (filters.end_date) filterBody.reportEndDate = filters.end_date;
      if (skipCache) filterBody.skipCache = true;

      const apiResponse = await fetchPostApi<ApiResponse<ReportHtmlInfo>>({
        elasticMetricType: 'get_report',
        body: filterBody
      });

      if (!skipCache) storeInCache(cacheKey, apiResponse);
      return apiResponse;
    } catch (err) {
      return {
        result: { errorMessage: 'Api Response error' } as ApiError,
        lastUpdated: ''
      };
    }
  };

  const fetchNonProdSecurity = async (
    skipCache = false
  ): Promise<ApiResponse<NonProdSecurityInfo>> => {
    try {
      const cacheKey = 'non-prod-security';
      if (!skipCache) {
        const cached = getCache(cacheKey);
        if (cached) return cached;
      }

      const apiResponse = await fetchPostApi<ApiResponse<NonProdSecurityInfo>>({
        elasticMetricType: 'waf-security',
        body: skipCache ? { skipCache: true } : {}
      });

      storeInCache(cacheKey, apiResponse);
      return apiResponse;
    } catch (err) {
      return {
        result: { errorMessage: 'Api Response error' } as ApiError,
        lastUpdated: ''
      };
    }
  };

  const fetchDeviceHealth = async (
    subType: string = 'device-errors',
    skipCache = false
  ): Promise<ApiResponse<DeviceHealthInfo>> => {
    try {
      const cacheKey = `device-health-${subType}`;
      if (!skipCache) {
        const cached = getCache(cacheKey);
        if (cached) return cached;
      }

      const apiResponse = await fetchPostApi<ApiResponse<DeviceHealthInfo>>({
        elasticMetricType: 'device-health',
        elasticMetricSubType: subType,
        body: skipCache ? { skipCache: true } : {}
      });

      storeInCache(cacheKey, apiResponse);
      return apiResponse;
    } catch (err) {
      return {
        result: { errorMessage: 'Api Response error' } as ApiError,
        lastUpdated: ''
      };
    }
  };

  const fetchErrorBreakdown = async (
    skipCache = false
  ): Promise<ApiResponse<ErrorBreakdownInfo>> => {
    try {
      const cacheKey = 'device-health-error-breakdown';
      if (!skipCache) {
        const cached = getCache(cacheKey);
        if (cached) return cached;
      }
      const apiResponse = await fetchPostApi<ApiResponse<ErrorBreakdownInfo>>({
        elasticMetricType: 'device-health',
        elasticMetricSubType: 'error-breakdown',
        body: skipCache ? { skipCache: true } : {}
      });
      storeInCache(cacheKey, apiResponse);
      return apiResponse;
    } catch (err) {
      return {
        result: { errorMessage: 'Api Response error' } as ApiError,
        lastUpdated: ''
      };
    }
  };

  const fetchEventDiagnostics = async (
    subType: string = 'error-events',
    skipCache = false
  ): Promise<ApiResponse<EventDiagnosticsInfo>> => {
    try {
      const cacheKey = `event-diagnostics-${subType}`;
      if (!skipCache) {
        const cached = getCache(cacheKey);
        if (cached) return cached;
      }

      const apiResponse = await fetchPostApi<ApiResponse<EventDiagnosticsInfo>>(
        {
          elasticMetricType: 'event-diagnostics',
          elasticMetricSubType: subType,
          body: skipCache ? { skipCache: true } : {}
        }
      );

      storeInCache(cacheKey, apiResponse);
      return apiResponse;
    } catch (err) {
      return {
        result: { errorMessage: 'Api Response error' } as ApiError,
        lastUpdated: ''
      };
    }
  };

  const fetchPerformanceTrends = async (
    subType: string = 'latency-vs-errors',
    skipCache = false
  ): Promise<ApiResponse<PerformanceTrendInfo>> => {
    try {
      const cacheKey = `performance-trends-${subType}`;
      if (!skipCache) {
        const cached = getCache(cacheKey);
        if (cached) return cached;
      }

      const apiResponse = await fetchPostApi<ApiResponse<PerformanceTrendInfo>>(
        {
          elasticMetricType: 'performance-trends',
          elasticMetricSubType: subType,
          body: skipCache ? { skipCache: true } : {}
        }
      );

      storeInCache(cacheKey, apiResponse);
      return apiResponse;
    } catch (err) {
      return {
        result: { errorMessage: 'Api Response error' } as ApiError,
        lastUpdated: ''
      };
    }
  };

  const fetchTemporal = async (
    skipCache = false
  ): Promise<ApiResponse<TemporalInfo>> => {
    try {
      return await fetchPostApi<ApiResponse<TemporalInfo>>({
        elasticMetricType: 'temporal',
        body: skipCache ? { skipCache: true } : {}
      });
    } catch (err) {
      return {
        result: { errorMessage: 'Failed to load workflow failures' } as ApiError,
        lastUpdated: ''
      };
    }
  };

  const fetchSyntheticCanaries = async (
    skipCache = false
  ): Promise<ApiResponse<SyntheticCanariesInfo>> => {
    try {
      const cacheKey = 'synthetic-canaries';
      if (!skipCache) {
        const cached = getCache(cacheKey);
        if (cached) return cached;
      }

      const apiResponse = await fetchPostApi<ApiResponse<SyntheticCanariesInfo>>(
        {
          elasticMetricType: 'synthetic-canaries',
          body: skipCache ? { skipCache: true } : {}
        }
      );

      storeInCache(cacheKey, apiResponse);
      return apiResponse;
    } catch (err) {
      return {
        result: {
          errorMessage: 'Failed to load synthetic canary health'
        } as ApiError,
        lastUpdated: ''
      };
    }
  };

  /**
   * App runtime config toggles. Deliberately NOT cached — a stale toggle state would
   * misrepresent production configuration, which is worse than a slower page.
   */
  const fetchAppToggles = async (): Promise<ApiResponse<AppTogglesInfo>> => {
    try {
      return await fetchPostApi<ApiResponse<AppTogglesInfo>>({
        elasticMetricType: 'AppToggles',
        type: 'getToggles'
      });
    } catch (err) {
      return {
        result: {
          errorMessage: isNotAuthorized(err)
            ? 'You are not authorized to view app runtime config'
            : 'Failed to load app runtime config'
        } as ApiError,
        lastUpdated: ''
      };
    }
  };

  /**
   * Applies a batch of flips as one AppConfig version + one deployment. Batched because
   * only one deployment can run per environment at a time.
   *
   * Lets errors propagate rather than flattening them into an ApiError: the caller has to
   * tell "reload, someone else changed it" from "wait, a rollout is running" from "you
   * were refused", and each needs a different message. Use `classifyToggleWriteError`.
   */
  const setAppToggles = async (
    changes: AppToggleChange[],
    expectedVersion: number,
    password: string
  ): Promise<ApiResponse<AppTogglesInfo>> =>
    fetchPostApi<ApiResponse<AppTogglesInfo>>({
      elasticMetricType: 'AppToggles',
      type: 'setToggles',
      body: {
        toggleChanges: changes,
        expectedVersion,
        adminPassword: password
      }
    });

  const updateAlerts = async (requestBody: AlertBody) => {
    const apiResponse = await fetchPostApi<ApiResponse<string>>({
      elasticMetricType: 'Alerts',
      isRequiredAllDetails: false,
      type: 'updateUserAlerts',
      body: requestBody
    });
    return apiResponse;
  };

  return {
    fetchApplications,
    fetchTransactions,
    fetchPayment,
    fetchApplicationStatus,
    fetchPageUsage,
    fetchAuthSessions,
    fetchTransactionInfo,
    fetchWebOutageInfo,
    fetchOutageScreenshots,
    fetchOutageScreenshotDetail,
    fetchAlertsInfo,
    updateAlerts,
    fetchReport,
    fetchNonProdSecurity,
    fetchDeviceHealth,
    fetchErrorBreakdown,
    fetchEventDiagnostics,
    fetchPerformanceTrends,
    fetchTemporal,
    fetchSyntheticCanaries,
    fetchAppToggles,
    setAppToggles
  };
};
